/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState, SVGProps } from 'react';
import { NavLink } from 'react-router-dom';
import { menu, power } from '../../../assets/svg';
import { SvgCollapseSidebar, SvgSettings } from '../../../assets/svg/general';
import { navLinks } from '../../../constants';
import { useAppDispatch, useAppSelector, useTenant } from '../../../hooks';
import { authSelector, logout } from '../../../store/slices';
import { LogoutLoader } from '../shared';

// Define types for navLinks
interface SubMenuItem {
  name: string;
  path: string;
  icon: string | ((props: SVGProps<SVGSVGElement>) => JSX.Element);
  restrictions: string[];
  exemptions?: string[];
  superAdminOnly?: boolean;
  tenantRestriction?: string;
}

interface NavLinkItem {
  name: string;
  path?: string;
  icon: string | ((props: SVGProps<SVGSVGElement>) => JSX.Element);
  restrictions: string[];
  exemptions?: string[];
  isSubmenu?: boolean;
  subMenuItems?: SubMenuItem[];
  superAdminOnly?: boolean;
  tenantRestriction?: string;
}

export const Sidebar = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(authSelector);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const { tenant, logoUrl } = useTenant();

  const Links = useRef<HTMLInputElement>(null);
  const submenuContainerRef = useRef<HTMLDivElement>(null);

  const [navOpen, setNavOpen] = useState<boolean>(false);
  const [navCollapsed, setNavCollapsed] = useState<boolean>(false);
  const [linksScrollable, setLinksScrollable] = useState(false);
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState<number | null>(null);

  const logoutUser = () => {
    setLogoutLoading(true);
    dispatch(logout()).finally(() => setLogoutLoading(false));
  };

  const toggleSidebar = () => {
    setNavOpen((prev) => !prev);
  };

  const handleCollapse = () => {
    setNavCollapsed((prev) => !prev);
  };

  const toggleSubmenu = (index: number) => {
    setOpenSubmenuIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  useEffect(() => {
    if (Links && Links.current) {
      setLinksScrollable(
        Links.current?.scrollHeight > Links.current?.clientHeight
      );
    }
  }, [Links, openSubmenuIndex]);

  // Handle click outside submenu when navigation is collapsed
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        navCollapsed &&
        openSubmenuIndex !== null &&
        submenuContainerRef.current &&
        !submenuContainerRef.current.contains(event.target as Node)
      ) {
        setOpenSubmenuIndex(null);
      }
    };

    if (navCollapsed && openSubmenuIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [navCollapsed, openSubmenuIndex]);

  // Helper function to check if a nav item should be shown based on user roles
  const shouldShowNavItem = (item: NavLinkItem | SubMenuItem) => {
    // Check super admin restriction
    if (item.superAdminOnly && !user?.admin?.isSuper) {
      return false;
    }

    // Check tenant restriction
    if (item.tenantRestriction && tenant?.slug !== item.tenantRestriction) {
      return false;
    }

    return (
      !user?.admin?.roles.some((role) => item.restrictions.includes(role)) ||
      (item.exemptions &&
        item.exemptions.includes('kitchenPartners') &&
        user?.admin?.kitchens)
    );
  };

  return (
    <div
      className={`relative ease-in-out transition-all duration-300 z-40 flex-shrink-0 ${
        navCollapsed ? 'lg:w-[5rem]' : 'lg:w-[15rem]'
      }`}
    >
      <div
        className={`bg-black absolute bg-opacity-10 min-h-screen h-full w-screen z-40 inset-0 ${
          navOpen ? 'block lg:hidden' : 'hidden'
        }
    `}
        onClick={toggleSidebar}
      ></div>
      <div
        className={`absolute lg:static left-0 top-0 transform ease-in-out z-50 transition-all duration-500 h-full bg-mild py-6 px-4 flex flex-col justify-between flex-shrink-0 border-r border-slate-medium lg:rounded-none ${
          navOpen ? 'translate-x-0' : '-translate-x-[15rem] lg:translate-x-0'
        } ${navCollapsed ? 'lg:w-full w-[15rem]' : 'w-[15rem]'}`}
      >
        <button
          className="h-14 flex flex-col justify-center items-end space-y-1 rounded-xl pr-2 w-12 absolute top-10 z-50 -right-5 bg-mild lg:hidden"
          onClick={toggleSidebar}
        >
          <img src={menu} className="-mr-2.5 h-7" alt="menu" />
        </button>
        {navCollapsed && (
          <button
            onClick={handleCollapse}
            className="hidden lg:flex absolute -right-3 top-8 z-50 w-6 h-6 items-center justify-center bg-white border border-slate-medium rounded-md shadow-sm hover:bg-neutral-200 focus:outline-none"
          >
            <svg
              width={12}
              height={12}
              viewBox="0 0 12 12"
              fill="none"
            >
              <path
                d="M4 2L8 6L4 10"
                stroke="#A5A7AC"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        <div className="h-[80%] flex flex-col">
          <div className={`w-full items-center flex px-2.5 mt-2 ${navCollapsed ? 'justify-center' : 'justify-between'}`}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={tenant?.name ?? 'logo'}
                className={`object-contain ${navCollapsed ? 'w-8 h-8' : 'h-8 max-w-[120px]'}`}
              />
            ) : (
              <p className={`bold text-lg text-text truncate ${navCollapsed ? 'lg:hidden' : ''}`}>
                {tenant?.name ?? 'Back Office'}
              </p>
            )}
            {!navCollapsed && (
              <button
                onClick={handleCollapse}
                className="hidden lg:block flex-shrink-0 focus:outline-none"
              >
                <SvgCollapseSidebar />
              </button>
            )}
          </div>
          <div
            className="flex flex-col mt-10 h-[85%] overflow-y-auto hide-scrollbar"
            ref={Links}
          >
            {(navLinks as NavLinkItem[]).map(
              (navItem, index) =>
                shouldShowNavItem(navItem) && (
                  <div key={navItem.name} className="mt-3">
                    {navItem.isSubmenu ? (
                      <div
                        className="relative"
                        ref={
                          openSubmenuIndex === index
                            ? submenuContainerRef
                            : null
                        }
                      >
                        <button
                          onClick={() => toggleSubmenu(index)}
                          className={`py-2 px-2.5 text-neutral-600 flex items-center justify-between w-full capitalize rounded-lg text-sm ${
                            openSubmenuIndex === index ? 'bg-neutral-200' : 'hover:bg-neutral-200'
                          } ${navCollapsed ? 'lg:justify-center' : ''}`}
                        >
                          <div className="flex items-center">
                            {typeof navItem.icon === 'string' ? (
                              <img
                                src={navItem.icon}
                                alt="icon"
                                className="w-4"
                              />
                            ) : (
                              // eslint-disable-next-line react/jsx-pascal-case
                              <navItem.icon className="w-5" />
                            )}
                            <p
                              className={`medium text-sm transition-all truncate delay-300 ml-3 ${
                                navCollapsed
                                  ? 'overflow-hidden lg:w-0 lg:opacity-0 lg:ml-0'
                                  : 'block'
                              }`}
                            >
                              {navItem.name}
                            </p>
                          </div>

                          <div
                            className={`transition-transform duration-300 ${
                              openSubmenuIndex === index ? 'rotate-180' : ''
                            } ${navCollapsed ? 'lg:hidden' : ''}`}
                          >
                            <svg
                              width="10"
                              height="6"
                              viewBox="0 0 10 6"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1 1L5 5L9 1"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </button>
                        <div
                          className={`flex flex-col items-start transition-all duration-300 ${
                            navCollapsed
                              ? `lg:absolute lg:left-[calc(100%+0.5rem)] lg:top-0 lg:w-56 lg:bg-white lg:rounded-lg lg:z-50 lg:shadow-lg lg:border lg:border-slate-medium lg:p-1.5 ${
                                  openSubmenuIndex === index
                                    ? 'max-h-96 lg:visible lg:opacity-100'
                                    : 'max-h-0 lg:invisible lg:opacity-0 overflow-hidden'
                                }`
                              : `overflow-hidden ${
                                  openSubmenuIndex === index ? 'max-h-96' : 'max-h-0'
                                }`
                          }`}
                        >
                          {navItem.subMenuItems?.map(
                            (subItem) =>
                              shouldShowNavItem(subItem) && (
                                <NavLink
                                  to={subItem.path}
                                  key={subItem.name}
                                  className={({ isActive }) =>
                                    `py-2 px-2.5 flex items-center capitalize rounded-lg text-sm mt-1 w-full ${
                                      isActive ? 'bg-primary-medium text-primary' : 'text-neutral-600 hover:bg-neutral-200'
                                    }`
                                  }
                                  onClick={toggleSidebar}
                                >
                                  <div className={`w-4 flex justify-center flex-shrink-0 ${navCollapsed ? 'lg:ml-0' : 'ml-4'}`}>
                                    {typeof subItem.icon === 'string' ? (
                                      <img
                                        src={subItem.icon}
                                        alt="icon"
                                        className="w-3"
                                      />
                                    ) : (
                                      // eslint-disable-next-line react/jsx-pascal-case
                                      <subItem.icon className="w-3" />
                                    )}
                                  </div>
                                  <p className="medium text-sm truncate ml-3">
                                    {subItem.name}
                                  </p>
                                </NavLink>
                              )
                          )}
                        </div>
                      </div>
                    ) : (
                      <NavLink
                        to={navItem.path || '#'}
                        className={({ isActive }) =>
                          `py-2 px-2.5 flex items-center capitalize rounded-lg text-sm ${
                            isActive ? 'bg-primary-medium text-primary' : 'text-neutral-600 hover:bg-neutral-200'
                          } ${navCollapsed ? 'lg:justify-center' : ''}`
                        }
                        onClick={toggleSidebar}
                      >
                        {typeof navItem.icon === 'string' ? (
                          <img src={navItem.icon} alt="icon" className="w-4" />
                        ) : (
                          // eslint-disable-next-line react/jsx-pascal-case
                          <navItem.icon className="w-4" />
                        )}
                        <p
                          className={`medium text-sm transition-all truncate delay-300 ml-3 ${
                            navCollapsed
                              ? 'overflow-hidden lg:w-0 lg:opacity-0 lg:ml-0'
                              : 'block'
                          }`}
                        >
                          {navItem.name}
                        </p>
                      </NavLink>
                    )}
                  </div>
                )
            )}
          </div>
          {linksScrollable && (
            <p className="animate-bounce text-xs text-center mt-2 text-neutral-400">
              scroll
            </p>
          )}
        </div>
        <div className="flex flex-col">
          <NavLink
            to="/dashboard/settings"
            className={({ isActive }) =>
              `py-2 px-2.5 flex items-center capitalize rounded-lg text-sm ${
                isActive ? 'bg-primary-medium text-primary' : 'text-neutral-600 hover:bg-neutral-200'
              } ${navCollapsed ? 'lg:justify-center' : ''}`
            }
          >
            <SvgSettings className="w-4" />
            <p
              className={`medium text-sm transition-all truncate delay-300 ml-3 ${
                navCollapsed
                  ? 'overflow-hidden lg:w-0 lg:opacity-0 lg:ml-0'
                  : 'block'
              }`}
            >
              settings
            </p>
          </NavLink>
        </div>
      </div>
    </div>
  );
};
